import { createContext, useEffect, useState, useMemo, useContext } from "react";
import { logEvent } from "firebase/analytics";
import { useLocation, useNavigate } from "react-router-dom";
import { isExpired } from "react-jwt";

//Hooks
import { useAuth } from "../hooks";

//Actions
import { authAction, getDistributionLeadsCount } from "actions";

// Utils
import handlePageView from "./global-ctx-helpers/handlePageView";
import analytics from "init/firebaseAnalitycs";

// Api
import { useGetInvoiceTrainingCounter } from "api/training/query";
import { useGetListInvoiceListingCount } from "api/invoice/query";
import { useGetAllAreasComplete, useGetListPropertyCategories, useGetListPropertyType } from "api/common/query";
import { useGetCountBuyerMatch } from "api/buyer-match/query";

export const GlobalContext = createContext();

const showIpaf = process.env.REACT_APP_SHOW_IPAF === "true";
const showOne21Event = process.env.REACT_APP_SHOW_ONE21 === "true";
export const GlobalProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { loginAction, logoutAction, tokenValidation, accessToken } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isHamburgerActive, setIsHamburgerActive] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [listingLeadsCounter, setListingLeadsCounter] = useState(null);

  const { data: invoiceTrainingCounter } = useGetInvoiceTrainingCounter({
    params: { billing_status: "waiting" },
  });
  const { data: invoiceListingCounter } = useGetListInvoiceListingCount({
    params: { invoice_status: "waiting" },
  });
  const { data: buyerMatchCounter } = useGetCountBuyerMatch({});

  const invoiceCounter = useMemo(() => {
    const training = invoiceTrainingCounter?.data?.data?.total ?? 0;
    const listing = invoiceListingCounter?.data?.data?.total ?? 0;

    return training + listing;
  }, [invoiceTrainingCounter, invoiceListingCounter]);

  const userRole = useMemo(() => {
    return {
      isMb: profile?.role?.name === "Member Broker",
      isDigitalOfficer4: profile?.role?.name === "Digital Officer #4",
      isVmb: profile?.role?.name === "Vice Member Broker",
    };
  }, [profile]);

  const showInvoice = useMemo(() => {
    return profile?.id !== 1196;
  }, [profile]);

  const showInvoiceAndEreporting = useMemo(() => {
    const arr = [37, 38];
    const roleId = profile?.role?.id;

    return !arr.includes(roleId);
  }, [profile]);

  const isRoleProjectAdmin = useMemo(() => {
    return profile?.role?.name === "Project Admin";
  }, [profile]);

  const { data: listAreaOnlyComplete } = useGetAllAreasComplete({ params: "" });
  const { data: listPropertyCategories } = useGetListPropertyCategories({ params: {page: 1, _limit: 30} });
  const { data: listPropertyType } = useGetListPropertyType({ params: {page: 1, _limit: 30} });

  const countBuyerMatch = useMemo(() => {
    return buyerMatchCounter?.data?.data?.total ?? null;
  }, [buyerMatchCounter]);

  const firebaseAnalytic = (eventName, eventParams) => {
    logEvent(analytics, eventName, eventParams);
  };

  const getDistributionLeads = async () => {
    try {
      const res = await getDistributionLeadsCount("?lead_status=menunggu");
      setListingLeadsCounter(res?.data?.data?.total ?? 0);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const getAuth = async () => {
      try {
        await authAction.getUserDetail().then((res) => {
          setProfile(res?.data ?? null);
          setLoading(false);
        });
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    if (!!accessToken?.token) {
      if (isExpired(accessToken?.token)) {
        logoutAction(() => navigate("/login"));
      } else {
        getAuth();
      }
    } else {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    const eventParams = handlePageView(location.pathname);
    firebaseAnalytic("page_view", eventParams);
  }, [location]);

  useEffect(() => {
    getDistributionLeads();
  }, []);

  if (loading) return null;
  return (
    <GlobalContext.Provider
      value={{
        loginAction,
        logoutAction,
        tokenValidation,
        accessToken,
        profile,
        isHamburgerActive,
        setIsHamburgerActive,
        showInvoice,
        userRole,
        showIpaf,
        showOne21Event,
        showLoading,
        setShowLoading,
        firebaseAnalytic,
        showInvoiceAndEreporting,
        listingLeadsCounter,
        getDistributionLeads,
        invoiceCounter,
        invoiceTrainingCounter: invoiceTrainingCounter?.data?.data?.total,
        invoiceListingCounter: invoiceListingCounter?.data?.data?.total,
        isRoleProjectAdmin,
        listAreaOnlyComplete: listAreaOnlyComplete?.data?.data  ?? [],
        listPropertyCategories: listPropertyCategories?.data?.data  ?? [],
        listPropertyType: listPropertyType?.data?.data  ?? [],
        countBuyerMatch
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalCtx = () => {
  const ctx = useContext(GlobalContext);

  if (!ctx) {
    throw new Error("useGlobalCtx must be used within the GlobalProvider");
  }

  return ctx;
};
